import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  LogOut, Plus, Edit, Trash2, Store, Package,
  Search, X, Save, Upload, Tag, Image as ImageIcon,
  BarChart3, MessageSquare, Settings, Receipt, Bell, Headphones, Camera,
  FileSpreadsheet, FileText, Download, UploadCloud, CheckCircle, AlertTriangle, Users, Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import CategoryManager from '../../components/admin/CategoryManager';
import SupportChat from '../../components/admin/SupportChat';
import BarcodeScanner from '../../components/BarcodeScanner';
import { playNotificationSound } from '../../utils/notificationSound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { storeDomain, adminPath } = useParams();
  const { user, loading: authLoading, logout, getAuthHeader, getAdminBasePath, hasPermission } = useAuth();
  const navigate = useNavigate();
  const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [notifications, setNotifications] = useState({ unread_chats: 0, pending_orders: 0, total: 0 });
  const fileInputRef = useRef(null);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const prevNotifRef = useRef({ chats: 0, orders: 0, support: 0 });
  const [showScanner, setShowScanner] = useState(false);
  const [toolsModuleEnabled, setToolsModuleEnabled] = useState(false);
  const [exportingInv, setExportingInv] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    cost_price: '',
    stock: '',
    image: '',
    description: '',
    barcode: '',
    tax_rate: 0
  });
  const [addStockData, setAddStockData] = useState({ add_quantity: '', new_cost_price: '' });

  const handleExportInventory = async (format) => {
    setExportingInv(format);
    try {
      const endpoint = format === 'excel' ? 'export-inventory-excel' : 'export-inventory-pdf';
      const response = await axios.get(`${API}/reports/${endpoint}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventario.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Inventario ${format.toUpperCase()} descargado`);
    } catch (error) {
      toast.error(`Error al exportar inventario`);
    } finally {
      setExportingInv(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/reports/inventory-template`, {
        headers: getAuthHeader(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch { toast.error('Error al descargar plantilla'); }
  };

  const handleImportProducts = async () => {
    if (!importFile) { toast.error('Selecciona un archivo'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await axios.post(`${API}/reports/import-products`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar');
      setImportResult({ error: err.response?.data?.detail || 'Error desconocido' });
    } finally {
      setImporting(false);
    }
  };

  const loadNotifications = useCallback(async () => {
    try {
      const [notifRes, supportRes] = await Promise.all([
        axios.get(`${API}/admin/notifications`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/support-unread`, { headers: getAuthHeader() })
      ]);
      const n = notifRes.data;
      const su = supportRes.data.unread || 0;
      
      // Play sounds for new items
      if (n.unread_chats > prevNotifRef.current.chats && prevNotifRef.current.chats >= 0) {
        playNotificationSound('message');
      }
      if (n.pending_orders > prevNotifRef.current.orders && prevNotifRef.current.orders >= 0) {
        playNotificationSound('order');
      }
      if (su > prevNotifRef.current.support && prevNotifRef.current.support >= 0) {
        playNotificationSound('message');
      }
      prevNotifRef.current = { chats: n.unread_chats, orders: n.pending_orders, support: su };
      
      setNotifications(n);
      setSupportUnread(su);
    } catch (e) {
      console.error('Notification load error:', e.message);
    }
  }, [getAuthHeader]);

  const loadData = useCallback(async () => {
    try {
      const storeId = user?.store_id;
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products?store_id=${storeId}`),
        axios.get(`${API}/categories?store_id=${storeId}`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      // Check if tools module is enabled
      try {
        const toolsRes = await axios.get(`${API}/tools/module-status`, { headers: getAuthHeader() });
        setToolsModuleEnabled(toolsRes.data.enabled);
      } catch (e) { /* module check failed, keep disabled */ }
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
      return;
    }
    // Check store mismatch: URL says one store, token says another → force re-login
    if (adminPath && user.admin_path && adminPath !== user.admin_path) {
      logout();
      navigate(`/${adminPath}/login`, { replace: true });
      return;
    }
    if (storeDomain && user.store_domain && storeDomain !== user.store_domain) {
      logout();
      navigate(`/admin/${storeDomain}/login`, { replace: true });
      return;
    }
    // Redirect kitchen role to kitchen display
    if (user.role === 'cocina') {
      const base = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());
      navigate(`${base}/kitchen`, { replace: true });
      return;
    }
    loadData();
    loadNotifications();
    const notifInterval = setInterval(loadNotifications, 10000);
    return () => clearInterval(notifInterval);
  }, [user, navigate, storeDomain, adminPath, loadData, loadNotifications, authLoading]);

  // Memoize expensive stats calculations
  const totalStock = useMemo(() => products.reduce((sum, p) => sum + (p.stock || 0), 0), [products]);
  const totalInvestment = useMemo(() => products.reduce((sum, p) => sum + (p.investment || (p.cost_price || 0) * (p.stock || 0)), 0), [products]);

  const handleLogout = () => {
    logout();
    navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
    toast.success('Sesión cerrada');
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setImageFile(null);
    setImagePreview('');
    setAddStockData({ add_quantity: '', new_cost_price: '' });
    setFormData({
      name: '',
      category: '',
      price: '',
      cost_price: '',
      stock: '',
      image: '',
      description: '',
      unit_type: 'unidad',
      barcode: '',
      tax_rate: 0
    });
    setIsModalOpen(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setImageFile(null);
    setImagePreview(product.image);
    setAddStockData({ add_quantity: '', new_cost_price: '' });
    setFormData({
      name: product.name,
      category: product.category,
      price: product.price.toString(),
      cost_price: (product.cost_price || 0).toString(),
      stock: (product.stock || 0).toString(),
      image: product.image,
      description: product.description,
      unit_type: product.unit_type || 'unidad',
      barcode: product.barcode || '',
      tax_rate: product.tax_rate || 0
    });
    setIsModalOpen(true);
  };

  const handleInventoryBarcodeScan = async (barcode) => {
    setShowScanner(false);
    try {
      const res = await axios.get(`${API}/products/barcode/${barcode}`, { headers: getAuthHeader() });
      const product = res.data;
      openEditModal(product);
      toast.success(`Producto encontrado: ${product.name}`);
    } catch (err) {
      if (err.response?.status === 404) {
        // Product not found - open create modal with barcode pre-filled
        setEditingProduct(null);
        setFormData({
          name: '', category: '', price: '', cost_price: '', stock: '',
          image: '', description: '', unit_type: 'unidad', barcode: barcode, tax_rate: 0
        });
        setImageFile(null);
        setImagePreview('');
        setIsModalOpen(true);
        toast.info(`Código ${barcode} no registrado. Crea el producto nuevo.`);
      } else {
        toast.error('Error buscando producto');
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setImageFile(null);
    setImagePreview('');
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', imageFile);

    try {
      const response = await axios.post(
        `${API}/upload-image`,
        formDataUpload,
        { 
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      // Backend returns full URL already
      return response.data.url;
    } catch (error) {
      toast.error('Error al subir imagen');
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let imageUrl = formData.image;

    // Upload new image if selected
    if (imageFile) {
      imageUrl = await uploadImage();
      if (!imageUrl) return;
    }

    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      cost_price: parseFloat(formData.cost_price) || 0,
      stock: parseFloat(formData.stock) || 0,
      image: imageUrl,
      unit_type: formData.unit_type || 'unidad',
      tax_rate: Number(formData.tax_rate) || 0
    };

    try {
      if (editingProduct) {
        // Update product info
        await axios.put(
          `${API}/products/${editingProduct.id}`,
          productData,
          { headers: getAuthHeader() }
        );
        
        // If adding stock, call the add-stock endpoint
        if (addStockData.add_quantity && parseFloat(addStockData.add_quantity) > 0) {
          const addData = { add_quantity: parseFloat(addStockData.add_quantity) };
          if (addStockData.new_cost_price && parseFloat(addStockData.new_cost_price) > 0) {
            addData.new_cost_price = parseFloat(addStockData.new_cost_price);
          }
          await axios.post(
            `${API}/products/${editingProduct.id}/add-stock`,
            addData,
            { headers: getAuthHeader() }
          );
        }
        toast.success('Producto actualizado');
      } else {
        // Create
        await axios.post(
          `${API}/products`,
          productData,
          { headers: getAuthHeader() }
        );
        toast.success('Producto creado');
      }
      
      closeModal();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
      console.error(error);
    }
  };

  const handleDelete = async (productId, productName) => {
    setDeleteConfirm({ id: productId, name: productName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(
        `${API}/products/${deleteConfirm.id}`,
        { headers: getAuthHeader() }
      );
      toast.success('Producto eliminado');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar producto');
      setDeleteConfirm(null);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Package size={48} className="mx-auto mb-4 text-accent-text animate-pulse" />
          <p className="body-large">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: 'var(--border-light)' }}>
        <div className="container py-3 md:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Store className="text-accent-text flex-shrink-0" size={28} />
              <div className="min-w-0">
                <h1 className="text-base md:text-lg font-bold truncate">Panel de Administración</h1>
                <p className="text-xs text-text-muted truncate">Bienvenido, {user?.username}</p>
              </div>
            </div>
            {/* Mobile: notification bell only in header row */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="relative" data-testid="notification-bell">
                <button
                  onClick={() => {
                    if (notifications.unread_chats > 0) navigate(`${basePath}/chat`);
                    else if (notifications.pending_orders > 0) navigate(`${basePath}/orders`);
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors relative"
                  title={`${notifications.unread_chats} chats sin leer, ${notifications.pending_orders} pedidos pendientes`}
                >
                  <Bell size={22} className={notifications.total > 0 ? 'text-accent-text' : 'text-text-muted'} />
                  {notifications.total > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold px-1"
                      data-testid="notification-count">
                      {notifications.total}
                    </span>
                  )}
                </button>
              </div>
              <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-gray-100 md:hidden" data-testid="nav-logout-mobile">
                <LogOut size={20} className="text-text-muted" />
              </button>
            </div>
          </div>
          {/* Scrollable Nav */}
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            <style>{`.flex.overflow-x-auto::-webkit-scrollbar{display:none}`}</style>
              {hasPermission(['admin', 'empleado', 'mesera']) && (
                <button 
                  onClick={() => navigate(`${basePath}/pos`)} 
                  className="btn-primary whitespace-nowrap text-sm flex-shrink-0"
                  data-testid="nav-pos-btn"
                >
                  <Receipt size={16} className="mr-1.5" />
                  POS
                </button>
              )}
              {hasPermission(['admin']) && (
                <button 
                  onClick={() => navigate(`${basePath}/stats`)} 
                  className="btn-secondary whitespace-nowrap text-sm flex-shrink-0"
                  data-testid="nav-stats-btn"
                >
                  <BarChart3 size={16} className="mr-1.5" />
                  Estadísticas
                </button>
              )}
              {hasPermission(['admin', 'empleado', 'mesera']) && (
                <button 
                  onClick={() => navigate(`${basePath}/chat`)} 
                  className="btn-secondary relative whitespace-nowrap text-sm flex-shrink-0"
                  data-testid="nav-chat-btn"
                >
                  <MessageSquare size={16} className="mr-1.5" />
                  Chat
                  {notifications.unread_chats > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold px-1"
                      data-testid="chat-badge">{notifications.unread_chats}</span>
                  )}
                </button>
              )}
              {hasPermission(['admin', 'empleado', 'mesera']) && (
                <button 
                  onClick={() => navigate(`${basePath}/orders`)} 
                  className="btn-secondary relative whitespace-nowrap text-sm flex-shrink-0"
                  data-testid="nav-orders-btn"
                >
                  <Package size={16} className="mr-1.5" />
                  Pedidos
                  {notifications.pending_orders > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold px-1"
                      data-testid="orders-badge">{notifications.pending_orders}</span>
                  )}
                </button>
              )}
              {hasPermission(['admin', 'empleado', 'mesera']) && (
                <button 
                  onClick={() => navigate(`${basePath}/customers`)} 
                  className="btn-secondary whitespace-nowrap text-sm flex-shrink-0"
                  data-testid="nav-customers-btn"
                >
                  <Users size={16} className="mr-1.5" />
                  Clientes
                </button>
              )}
              <button 
                onClick={() => setShowSupportChat(true)} 
                className="btn-secondary relative whitespace-nowrap text-sm flex-shrink-0"
                data-testid="nav-support-btn"
              >
                <Headphones size={16} className="mr-1.5" />
                Soporte
                {supportUnread > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold px-1"
                    data-testid="support-badge">{supportUnread}</span>
                )}
              </button>
              {hasPermission(['admin']) && (
                <button 
                  onClick={() => navigate(`${basePath}/settings`)} 
                  className="btn-secondary whitespace-nowrap text-sm flex-shrink-0"
                  data-testid="nav-settings-btn"
                >
                  <Settings size={16} className="mr-1.5" />
                  Config
                </button>
              )}
              {toolsModuleEnabled && (
                <button onClick={() => navigate(`${basePath}/tools`)}
                  className="btn-secondary whitespace-nowrap text-sm flex-shrink-0"
                  data-testid="nav-tools-btn">
                  <Wrench size={16} className="mr-1.5" />
                  Herramientas
                </button>
              )}
              <button onClick={handleLogout} className="btn-secondary whitespace-nowrap text-sm flex-shrink-0 hidden md:flex" data-testid="nav-logout-btn">
                <LogOut size={16} className="mr-1.5" />
                Salir
              </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-5 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <p className="text-sm text-text-muted mb-1">Total Productos</p>
            <p className="text-2xl font-bold text-accent-text">{products.length}</p>
          </div>
          <div className="bg-white p-5 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <p className="text-sm text-text-muted mb-1">Stock Total</p>
            <p className="text-2xl font-bold text-accent-text">
              {totalStock}
            </p>
          </div>
          {hasPermission(['admin']) && (
            <div className="bg-white p-5 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
              <p className="text-sm text-text-muted mb-1">Inversión Total</p>
              <p className="text-2xl font-bold text-blue-600">
                ${totalInvestment.toFixed(2)}
              </p>
            </div>
          )}
          <div className="bg-white p-5 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <p className="text-sm text-text-muted mb-1">Categorías</p>
            <p className="text-2xl font-bold text-accent-text">{categories.length}</p>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white p-4 rounded-xl border mb-6" style={{ borderColor: 'var(--border-light)' }}>
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-3 flex-1">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                  style={{
                    borderColor: 'var(--border-light)',
                    '--tw-ring-color': 'var(--accent-primary)'
                  }}
                />
              </div>

              {/* Category Filter */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                  '--tw-ring-color': 'var(--accent-primary)'
                }}
              >
                <option value="">Todas las categorías</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 flex-wrap">
              {hasPermission(['admin']) && (
                <button onClick={() => handleExportInventory('excel')} disabled={exportingInv === 'excel'}
                  className="px-3 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1"
                  style={{ background: exportingInv === 'excel' ? '#9ca3af' : '#10B981' }}
                  data-testid="export-inventory-excel">
                  <FileSpreadsheet size={16} /> Excel
                </button>
              )}
              {hasPermission(['admin']) && (
                <button onClick={() => handleExportInventory('pdf')} disabled={exportingInv === 'pdf'}
                  className="px-3 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1"
                  style={{ background: exportingInv === 'pdf' ? '#9ca3af' : '#EF4444' }}
                  data-testid="export-inventory-pdf">
                  <FileText size={16} /> PDF
                </button>
              )}
              {hasPermission(['admin']) && (
                <button onClick={() => { setShowImportModal(true); setImportFile(null); setImportResult(null); }}
                  className="px-3 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1"
                  style={{ background: '#7C3AED' }}
                  data-testid="import-products-btn">
                  <UploadCloud size={16} /> Importar
                </button>
              )}
              {hasPermission(['admin', 'inventario']) && (
                <button onClick={() => setIsCategoryManagerOpen(true)} className="btn-secondary" data-testid="manage-categories-btn">
                  <Tag size={20} className="mr-2" />
                  Categorías
                </button>
              )}
              {hasPermission(['admin', 'inventario']) && (
                <button onClick={() => setShowScanner(true)} className="btn-secondary" data-testid="scan-inventory-btn">
                  <Camera size={20} className="mr-2" />
                  Escanear
                </button>
              )}
              {hasPermission(['admin', 'inventario']) && (
                <button onClick={openAddModal} className="btn-primary" data-testid="add-product-btn">
                  <Plus size={20} className="mr-2" />
                  Nuevo Producto
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-light)' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-section">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-text-primary">Imagen</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-text-primary">Nombre</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-text-primary">Categoría</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-text-primary">Stock</th>
                  {hasPermission(['admin']) && <th className="px-4 py-4 text-left text-sm font-semibold text-text-primary">Costo</th>}
                  {hasPermission(['admin']) && <th className="px-4 py-4 text-left text-sm font-semibold text-text-primary">Inversión</th>}
                  <th className="px-4 py-4 text-left text-sm font-semibold text-text-primary">Precio</th>
                  <th className="px-4 py-4 text-left text-sm font-semibold text-text-primary">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, index) => (
                  <tr 
                    key={product.id}
                    className="border-t hover:bg-section transition-colors"
                    style={{ borderColor: 'var(--border-light)' }}
                  >
                    <td className="px-4 py-4">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-14 h-14 object-cover rounded-lg"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <p className="font-medium text-text-primary">{product.name}</p>
                      <p className="text-xs text-text-muted truncate max-w-[180px]">{product.description}</p>
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-1 rounded-full text-xs font-medium" 
                        style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
                        {categories.find(c => c.id === product.category)?.name || product.category}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span 
                        className={`px-2 py-1 rounded-full text-sm font-medium ${
                          (product.stock || 0) < 10 
                            ? 'bg-orange-100 text-orange-600' 
                            : 'bg-green-100 text-green-600'
                        }`}
                      >
                        {product.stock || 0}
                      </span>
                    </td>
                    {hasPermission(['admin']) && (
                      <td className="px-4 py-4 text-text-muted text-sm">
                        ${(product.cost_price || 0).toFixed(2)}
                      </td>
                    )}
                    {hasPermission(['admin']) && (
                      <td className="px-4 py-4">
                        <span className="font-semibold text-blue-600">
                          ${(product.investment || (product.cost_price || 0) * (product.stock || 0)).toFixed(2)}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <span className="font-bold text-lg" style={{ color: 'var(--accent-text)' }}>
                        ${product.price.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        {hasPermission(['admin', 'inventario']) && (
                          <button
                            onClick={() => openEditModal(product)}
                            className="p-2 hover:bg-accent-wash rounded-lg transition-colors"
                            title="Editar"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit size={18} className="text-accent-text" />
                          </button>
                        )}
                        {hasPermission(['admin', 'inventario']) && (
                          <button
                            onClick={() => handleDelete(product.id, product.name)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 size={18} className="text-red-500" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <Package size={48} className="mx-auto mb-4 text-text-muted" />
                <p className="body-large text-text-muted">No hay productos</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={closeModal}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex items-center justify-between">
                  <h2 className="heading-3">
                    {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
                  </h2>
                  <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X size={24} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Nombre del Producto *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      borderColor: 'var(--border-light)',
                      '--tw-ring-color': 'var(--accent-primary)'
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Categoría *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      borderColor: 'var(--border-light)',
                      '--tw-ring-color': 'var(--accent-primary)'
                    }}
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Unidad de Venta
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: 'unidad', label: 'Unidad', sub: 'ud' },
                      { value: 'libra', label: 'Libra', sub: 'lb' },
                      { value: 'kilo', label: 'Kilo', sub: 'kg' },
                      { value: 'gramo', label: 'Gramo', sub: 'g' }
                    ].map(u => (
                      <button key={u.value} type="button"
                        onClick={() => setFormData({...formData, unit_type: u.value})}
                        data-testid={`unit-type-${u.value}`}
                        className="py-2 px-3 rounded-lg border text-center transition-all text-sm"
                        style={{
                          borderColor: formData.unit_type === u.value ? 'var(--accent-primary)' : 'var(--border-light)',
                          background: formData.unit_type === u.value ? 'var(--accent-wash)' : 'white',
                          color: formData.unit_type === u.value ? 'var(--accent-text)' : 'var(--text-primary)',
                          fontWeight: formData.unit_type === u.value ? 600 : 400
                        }}>
                        {u.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Precio de Venta {formData.unit_type !== 'unidad' ? `($ por ${formData.unit_type === 'libra' ? 'lb' : formData.unit_type === 'kilo' ? 'kg' : formData.unit_type === 'gramo' ? 'g' : 'ud'})` : '($)'} *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      required
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        borderColor: 'var(--border-light)',
                        '--tw-ring-color': 'var(--accent-primary)'
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Costo de Adquisición ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.cost_price}
                      onChange={(e) => setFormData({...formData, cost_price: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        borderColor: 'var(--border-light)',
                        '--tw-ring-color': 'var(--accent-primary)'
                      }}
                    />
                  </div>
                </div>

                {/* Profit Display */}
                {formData.price && formData.cost_price && parseFloat(formData.price) > 0 && parseFloat(formData.cost_price) > 0 && (
                  <div className="p-4 rounded-lg" style={{ background: 'var(--accent-wash)' }}>
                    <p className="text-sm font-medium text-accent-text">
                      Ganancia por {formData.unit_type !== 'unidad' ? (formData.unit_type === 'libra' ? 'libra' : formData.unit_type === 'kilo' ? 'kilo' : 'gramo') : 'unidad'}: ${(parseFloat(formData.price) - parseFloat(formData.cost_price)).toFixed(2)}
                      {' '}({(((parseFloat(formData.price) - parseFloat(formData.cost_price)) / parseFloat(formData.cost_price)) * 100).toFixed(1)}%)
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Stock Disponible {formData.unit_type !== 'unidad' ? `(${formData.unit_type === 'libra' ? 'libras' : formData.unit_type === 'kilo' ? 'kilos' : 'gramos'})` : ''} *
                  </label>
                  {editingProduct ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="text-xs text-text-muted mb-1 block">Stock actual</label>
                          <input
                            type="number"
                            step={formData.unit_type !== 'unidad' ? '0.01' : '1'}
                            value={formData.stock}
                            disabled
                            className="w-full px-4 py-3 rounded-lg border bg-gray-50 text-text-muted"
                            style={{ borderColor: 'var(--border-light)' }}
                            data-testid="current-stock-input"
                          />
                        </div>
                        <div className="text-2xl text-text-muted pt-5">+</div>
                        <div className="flex-1">
                          <label className="text-xs text-accent-text font-medium mb-1 block">Agregar {formData.unit_type !== 'unidad' ? (formData.unit_type === 'libra' ? 'libras' : formData.unit_type === 'kilo' ? 'kilos' : 'gramos') : 'unidades'}</label>
                          <input
                            type="number"
                            step={formData.unit_type !== 'unidad' ? '0.01' : '1'}
                            min="0"
                            value={addStockData.add_quantity}
                            onChange={(e) => setAddStockData({...addStockData, add_quantity: e.target.value})}
                            className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                            style={{
                              borderColor: 'var(--accent-primary)',
                              '--tw-ring-color': 'var(--accent-primary)',
                              background: 'var(--accent-wash)'
                            }}
                            placeholder="0"
                            data-testid="add-stock-input"
                          />
                        </div>
                        <div className="text-2xl text-text-muted pt-5">=</div>
                        <div className="flex-1">
                          <label className="text-xs text-text-muted mb-1 block">Stock final</label>
                          <div className="w-full px-4 py-3 rounded-lg border bg-green-50 text-green-700 font-bold text-center"
                            style={{ borderColor: '#86efac' }}
                            data-testid="final-stock-display"
                          >
                            {((parseFloat(formData.stock) || 0) + (parseFloat(addStockData.add_quantity) || 0)).toFixed(formData.unit_type !== 'unidad' ? 2 : 0)}
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-accent-text font-medium mb-1 block">Nuevo costo de adquisición (opcional)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={addStockData.new_cost_price}
                          onChange={(e) => setAddStockData({...addStockData, new_cost_price: e.target.value})}
                          className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                          style={{
                            borderColor: 'var(--border-light)',
                            '--tw-ring-color': 'var(--accent-primary)'
                          }}
                          placeholder={`Actual: $${formData.cost_price || '0.00'}`}
                          data-testid="new-cost-price-input"
                        />
                        <p className="text-xs text-text-muted mt-1">Si el nuevo lote tiene diferente precio de compra, ingréselo aquí</p>
                      </div>
                    </div>
                  ) : (
                    <input
                      type="number"
                      step={formData.unit_type !== 'unidad' ? '0.01' : '1'}
                      min="0"
                      value={formData.stock}
                      onChange={(e) => setFormData({...formData, stock: e.target.value})}
                      required
                      className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        borderColor: 'var(--border-light)',
                        '--tw-ring-color': 'var(--accent-primary)'
                      }}
                      placeholder="Cantidad disponible"
                      data-testid="stock-input"
                    />
                  )}
                  {!editingProduct && formData.stock && parseInt(formData.stock) < 10 && (
                    <p className="text-sm text-orange-500 mt-1">
                      Stock bajo - considera reabastecer
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Imagen del Producto *
                  </label>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mb-3 relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-lg border"
                        style={{ borderColor: 'var(--border-light)' }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview('');
                          setFormData({...formData, image: ''});
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn-secondary flex-1"
                    >
                      <Upload size={18} className="inline mr-2" />
                      {imageFile ? 'Cambiar Imagen' : 'Subir desde PC'}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </div>
                  
                  {imageFile && (
                    <p className="text-sm text-accent-text mt-2">
                      <ImageIcon size={14} className="inline" /> {imageFile.name}
                    </p>
                  )}

                  {/* URL Input (alternative) */}
                  <div className="mt-3">
                    <label className="text-xs text-text-muted mb-1 block">O ingresa URL de imagen:</label>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => {
                        setFormData({...formData, image: e.target.value});
                        setImagePreview(e.target.value);
                        setImageFile(null);
                      }}
                      className="w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2"
                      style={{
                        borderColor: 'var(--border-light)',
                        '--tw-ring-color': 'var(--accent-primary)'
                      }}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Descripción *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows="3"
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      borderColor: 'var(--border-light)',
                      '--tw-ring-color': 'var(--accent-primary)'
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Código de Barras (opcional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.barcode || ''}
                      onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                      placeholder="Escanea o escribe el código"
                      className="flex-1 px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                      style={{ borderColor: 'var(--border-light)', '--tw-ring-color': 'var(--accent-primary)' }}
                      data-testid="barcode-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowScanner(true)}
                      className="px-4 py-3 rounded-lg text-white font-medium"
                      style={{ background: '#10b981' }}
                      data-testid="scan-barcode-form-btn"
                    >
                      <Camera size={20} />
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="btn-primary flex-1"
                    disabled={uploading}
                    style={{ opacity: uploading ? 0.6 : 1 }}
                  >
                    <Save size={20} className="inline mr-2" />
                    {uploading ? 'Subiendo...' : editingProduct ? 'Actualizar Producto' : 'Crear Producto'}
                  </button>
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Category Manager Modal */}
      <CategoryManager 
        isOpen={isCategoryManagerOpen}
        onClose={() => setIsCategoryManagerOpen(false)}
        onCategoriesUpdated={loadData}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setDeleteConfirm(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full" data-testid="delete-confirm-modal">
              <div className="text-center mb-4">
                <Trash2 size={40} className="mx-auto text-red-500 mb-3" />
                <h3 className="font-bold text-lg text-text-primary">Eliminar Producto</h3>
                <p className="text-text-muted mt-2">
                  ¿Estás seguro de eliminar <strong>&ldquo;{deleteConfirm.name}&rdquo;</strong>? Esta acción no se puede deshacer.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn-secondary flex-1"
                  data-testid="delete-cancel-btn"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2 rounded-lg text-white font-medium transition-colors"
                  style={{ background: '#ef4444' }}
                  data-testid="delete-confirm-btn"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Barcode Scanner */}
      {showScanner && (
        <BarcodeScanner
          onScan={(barcode) => {
            if (isModalOpen) {
              // If form is open, just fill the barcode field
              setShowScanner(false);
              setFormData(prev => ({ ...prev, barcode }));
              toast.success(`Código escaneado: ${barcode}`);
            } else {
              handleInventoryBarcodeScan(barcode);
            }
          }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Support Chat */}
      {showSupportChat && (
        <SupportChat onClose={() => { setShowSupportChat(false); loadNotifications(); }} />
      )}

      {/* Import Products Modal */}
      {showImportModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowImportModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" data-testid="import-modal">
              <div className="p-6 border-b" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold flex items-center gap-2">
                    <UploadCloud size={22} style={{ color: '#7C3AED' }} />
                    Importar Productos desde Excel
                  </h2>
                  <button onClick={() => setShowImportModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
                </div>
              </div>
              <div className="p-6 space-y-5">
                {/* Step 1: Download Template */}
                <div className="border rounded-lg p-4" style={{ borderColor: 'var(--border-light)' }}>
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: '#7C3AED' }}>1</span>
                    <div className="flex-1">
                      <p className="font-bold text-sm">Descarga la plantilla</p>
                      <p className="text-xs text-text-muted mt-1">Tiene las columnas correctas y tus categorias existentes</p>
                      <button onClick={handleDownloadTemplate}
                        className="mt-2 px-4 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 hover:bg-gray-50"
                        style={{ borderColor: 'var(--border-light)' }}
                        data-testid="download-template-btn">
                        <Download size={16} style={{ color: '#7C3AED' }} /> Descargar Plantilla
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step 2: Fill and Upload */}
                <div className="border rounded-lg p-4" style={{ borderColor: 'var(--border-light)' }}>
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: '#7C3AED' }}>2</span>
                    <div className="flex-1">
                      <p className="font-bold text-sm">Sube tu archivo</p>
                      <p className="text-xs text-text-muted mt-1">Llena la plantilla con tus productos y subela aqui</p>
                      <div className="mt-2">
                        {importFile ? (
                          <div className="flex items-center gap-2 p-3 rounded-lg border" style={{ borderColor: '#7C3AED', background: '#FAF5FF' }}>
                            <FileSpreadsheet size={20} style={{ color: '#7C3AED' }} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{importFile.name}</p>
                              <p className="text-xs text-text-muted">{(importFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <button onClick={() => setImportFile(null)} className="p-1 hover:bg-white rounded"><X size={16} className="text-gray-400" /></button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center gap-2 p-6 rounded-lg border-2 border-dashed cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-all"
                            style={{ borderColor: '#d1d5db' }} data-testid="import-file-dropzone">
                            <UploadCloud size={32} style={{ color: '#9ca3af' }} />
                            <p className="text-sm text-text-muted">Click para seleccionar archivo Excel</p>
                            <input ref={importFileRef} type="file" accept=".xlsx,.xls" className="hidden"
                              onChange={(e) => { if (e.target.files[0]) setImportFile(e.target.files[0]); }} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3: Import */}
                <div className="border rounded-lg p-4" style={{ borderColor: 'var(--border-light)' }}>
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: '#7C3AED' }}>3</span>
                    <div className="flex-1">
                      <p className="font-bold text-sm">Importar</p>
                      <p className="text-xs text-text-muted mt-1">Los productos existentes se actualizan, los nuevos se crean</p>
                      <button onClick={handleImportProducts} disabled={!importFile || importing}
                        className="mt-2 w-full py-3 rounded-lg text-white font-bold flex items-center justify-center gap-2"
                        style={{ background: (!importFile || importing) ? '#d1d5db' : '#7C3AED' }}
                        data-testid="import-submit-btn">
                        <UploadCloud size={18} />
                        {importing ? 'Importando...' : 'Importar Productos'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Results */}
                {importResult && !importResult.error && (
                  <div className="border rounded-lg p-4 space-y-2" style={{ borderColor: '#22c55e', background: '#f0fdf4' }} data-testid="import-result">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={20} style={{ color: '#22c55e' }} />
                      <p className="font-bold text-sm" style={{ color: '#166534' }}>{importResult.message}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 rounded bg-white"><span className="font-bold text-green-600">{importResult.created}</span> <span className="text-text-muted">creados</span></div>
                      <div className="p-2 rounded bg-white"><span className="font-bold text-blue-600">{importResult.updated}</span> <span className="text-text-muted">actualizados</span></div>
                    </div>
                    {importResult.categories_created?.length > 0 && (
                      <p className="text-xs text-text-muted">Categorias creadas: {importResult.categories_created.join(', ')}</p>
                    )}
                    {importResult.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-red-600 flex items-center gap-1"><AlertTriangle size={14} /> {importResult.errors.length} errores:</p>
                        <div className="max-h-24 overflow-y-auto mt-1">
                          {importResult.errors.map((err, i) => (
                            <p key={i} className="text-xs text-red-500">Fila {err.row}: {err.field} - {err.message}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {importResult?.error && (
                  <div className="border rounded-lg p-4" style={{ borderColor: '#ef4444', background: '#fef2f2' }}>
                    <p className="text-sm text-red-600 font-medium flex items-center gap-2"><AlertTriangle size={16} /> {importResult.error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile Floating Messages Button */}
      {!showSupportChat && (
        <div className="fixed bottom-16 right-4 z-40 flex flex-col gap-2 md:hidden" data-testid="mobile-fab-messages">
          {/* Configuración - solo admin */}
          {hasPermission(['admin']) && (
            <button
              onClick={() => navigate(`${basePath}/settings`)}
              className="w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white"
              style={{ background: '#6b7280' }}
              data-testid="fab-settings"
            >
              <Settings size={22} />
            </button>
          )}
          {/* Soporte */}
          <button
            onClick={() => setShowSupportChat(true)}
            className="relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white"
            style={{ background: '#6366f1' }}
            data-testid="fab-support"
          >
            <Headphones size={22} />
            {supportUnread > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{supportUnread}</span>
            )}
          </button>
          {/* Chat clientes */}
          <button
            onClick={() => navigate(`${basePath}/chat`)}
            className="relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white"
            style={{ background: '#3b82f6' }}
            data-testid="fab-chat"
          >
            <MessageSquare size={22} />
            {notifications.unread_chats > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{notifications.unread_chats}</span>
            )}
          </button>
          {/* Pedidos */}
          <button
            onClick={() => navigate(`${basePath}/orders`)}
            className="relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white"
            style={{ background: '#f97316' }}
            data-testid="fab-orders"
          >
            <Package size={22} />
            {notifications.pending_orders > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{notifications.pending_orders}</span>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
